import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  Progress,
  Tag,
  Divider,
  message,
  Modal,
  Form,
  InputNumber,
  Tooltip,
} from 'antd';
import {
  DollarOutlined,
  TrophyOutlined,
  BankOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { deliveryService } from '../services/deliveryService';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Earnings = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [achievementsModalVisible, setAchievementsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const response = await deliveryService.getEarnings();
      if (response.data.success) {
        // Map API response to expected format
        const earningsData = response.data.data;

        // Use real API data
        const walletBalanceValue = parseFloat(earningsData.wallet_balance || 0);

        // Build transaction rows from individual order earnings
        const orderRows = (earningsData.order_earnings || []).map((order) => ({
          id: order.id,
          order_id: `ORD-${order.id}`, // Use id to create order_id since order_id field doesn't exist
          customer_name: order.customer?.name, // Don't set fallback here, let table render handle it
          delivery_fee: parseFloat(order.delivery_charge || 0),
          tip: 0, // Tip field not available in Order model yet
          bonus: 0, // Bonus field not available in Order model yet
          total: parseFloat(order.total_amount || order.delivery_charge || 0),
          status: order.order_status || 'completed',
          created_at: order.created_at,
        }));

        // Set earnings data - use individual orders
        setEarnings(orderRows);
        setWalletBalance(walletBalanceValue);
      } else {
        setEarnings([]);
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      message.error('Failed to load earnings');
      setEarnings([]);
    } finally {
      setLoading(false);
    }
  };

  const totalEarnings =
    Array.isArray(earnings) && earnings.length > 0
      ? earnings.reduce((sum, e) => sum + (e.total || 0), 0)
      : 0;
  const totalDeliveries = Array.isArray(earnings) ? earnings.length : 0;
  const totalTips =
    Array.isArray(earnings) && earnings.length > 0
      ? earnings.reduce((sum, e) => sum + (e.tip || 0), 0)
      : 0;
  const totalBonuses =
    Array.isArray(earnings) && earnings.length > 0
      ? earnings.reduce((sum, e) => sum + (e.bonus || 0), 0)
      : 0;
  const averageTip = totalDeliveries > 0 ? totalTips / totalDeliveries : 0;

  const handleWithdraw = async (values) => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      message.success(`Withdrawal request for NPR ${values.amount} submitted successfully!`);
      setWithdrawModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to submit withdrawal request. Please try again.');
    }
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const reportHTML = `
        <html>
          <head>
            <title>Earnings Report - ${user?.name || 'Delivery Person'}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; }
              h1 { color: #1890ff; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background: #1890ff; color: white; }
              .summary { margin: 20px 0; }
              .summary-item { margin: 10px 0; font-size: 16px; }
              .footer { margin-top: 40px; font-size: 12px; color: #666; }
            </style>
          </head>
          <body>
            <h1>Current Care - Earnings Report</h1>
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Delivery Person: ${user?.name || 'N/A'}</p>
            <div class="summary">
              <div class="summary-item"><strong>Total Earnings:</strong> NPR ${(totalEarnings || 0).toFixed(2)}</div>
              <div class="summary-item"><strong>Total Deliveries:</strong> ${totalDeliveries}</div>
              <div class="summary-item"><strong>Total Tips:</strong> NPR ${(totalTips || 0).toFixed(2)}</div>
              <div class="summary-item"><strong>Total Bonuses:</strong> NPR ${(totalBonuses || 0).toFixed(2)}</div>
              <div class="summary-item"><strong>Wallet Balance:</strong> NPR ${(walletBalance || 0).toFixed(2)}</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Orders</th>
                  <th>Delivery Fee</th>
                  <th>Tip</th>
                  <th>Bonus</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${(earnings || [])
                  .map(
                    (e) => `
                  <tr>
                    <td>${e.order_id || '-'}</td>
                    <td>${e.customer_name || '-'}</td>
                    <td>NPR ${(e.delivery_fee || 0).toFixed(2)}</td>
                    <td>NPR ${(e.tip || 0).toFixed(2)}</td>
                    <td>NPR ${(e.bonus || 0).toFixed(2)}</td>
                    <td><strong>NPR ${(e.total || 0).toFixed(2)}</strong></td>
                  </tr>
                `,
                  )
                  .join('')}
              </tbody>
            </table>
            <div class="footer">
              <p>This report was generated by Current Care Delivery System.</p>
            </div>
          </body>
        </html>
      `;
      printWindow.document.write(reportHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
      message.success('Earnings report opened. Use print dialog to save as PDF.');
    } else {
      message.error('Unable to open print window. Please allow popups.');
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (
        <div>
          <Text strong>
            {date
              ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : '-'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {date ? new Date(date).toLocaleDateString('en-US', { weekday: 'short' }) : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Order',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 100,
      render: (text) => (
        <div>
          <Text strong style={{ color: '#1890ff' }}>
            {text}
          </Text>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 150,
      render: (name, record) => (
        <div>
          <Text strong>{name || `Customer #${record.customer_id || record.id}`}</Text>
          {record.customer?.phone && <br />}
          {record.customer?.phone && (
            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
              {record.customer.phone}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Earnings',
      key: 'earnings',
      width: 200,
      render: (_, record) => (
        <div style={{ textAlign: 'right' }}>
          <div>
            <Text style={{ fontSize: '0.9rem', color: '#666' }}>Fee: </Text>
            <Text>NPR {(record.delivery_fee || 0).toFixed(2)}</Text>
          </div>
          {(record.tip > 0 || record.bonus > 0) && (
            <>
              {record.tip > 0 && (
                <div>
                  <Text style={{ fontSize: '0.9rem', color: '#52c41a' }}>Tip: </Text>
                  <Text style={{ color: '#52c41a' }}>NPR {(record.tip || 0).toFixed(2)}</Text>
                </div>
              )}
              {record.bonus > 0 && (
                <div>
                  <Text style={{ fontSize: '0.9rem', color: '#faad14' }}>Bonus: </Text>
                  <Text style={{ color: '#faad14' }}>NPR {(record.bonus || 0).toFixed(2)}</Text>
                </div>
              )}
            </>
          )}
          <Divider style={{ margin: '4px 0' }} />
          <Text strong style={{ color: '#1890ff', fontSize: '1rem' }}>
            NPR {(record.total || 0).toFixed(2)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === 'delivered' ? 'success' : status === 'pending' ? 'warning' : 'processing'
          }
          style={{ fontWeight: 'bold' }}
        >
          {status === 'delivered' ? '✓ Delivered' : status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
  ];

  const achievements = [
    {
      id: 1,
      title: 'Speed Demon',
      description: 'Complete 10 deliveries in under 30 minutes each',
      progress: 7,
      total: 10,
      reward: 'NPR 50 Bonus',
      achieved: false,
    },
    {
      id: 2,
      title: 'Customer Favorite',
      description: 'Receive 20 5-star ratings',
      progress: 15,
      total: 20,
      reward: 'NPR 100 Bonus',
      achieved: false,
    },
    {
      id: 3,
      title: 'Early Bird',
      description: 'Complete 5 deliveries before 9 AM',
      progress: 5,
      total: 5,
      reward: 'NPR 25 Bonus',
      achieved: true,
    },
    {
      id: 4,
      title: 'Weekend Warrior',
      description: 'Complete 15 weekend deliveries',
      progress: 12,
      total: 15,
      reward: 'NPR 75 Bonus',
      achieved: false,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .table-row-light:hover,
        .table-row-dark:hover {
          background-color: #f0f8ff !important;
        }
        .ant-table-thead > tr > th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #e9ecef;
          font-weight: 600;
          color: #495057;
        }
        .ant-table-tbody > tr > td {
          padding: 16px;
          vertical-align: top;
        }
      `}</style>
      <Title level={2}>Earnings</Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Track your delivery earnings, tips, bonuses, and manage withdrawals
      </Paragraph>

      {/* Earnings Stats */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Earnings"
              value={totalEarnings}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Deliveries"
              value={totalDeliveries}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Tips"
              value={totalTips}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Average Tip"
              value={averageTip}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
              suffix="NPR"
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ textAlign: 'center', height: '100%' }}>
            <BankOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '16px' }} />
            <Tooltip title="Total amount in your wallet from all deliveries">
              <Title level={5}>Wallet Balance</Title>
            </Tooltip>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#1890ff',
                marginBottom: '16px',
              }}
            >
              NPR {(walletBalance || 0).toFixed(2)}
            </div>
            <Paragraph style={{ fontSize: '0.8rem', color: '#999', marginBottom: '12px' }}>
              Total wallet balance from all deliveries
            </Paragraph>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setWithdrawModalVisible(true)}
            >
              Withdraw Funds
            </Button>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ textAlign: 'center', height: '100%' }}>
            <DownloadOutlined
              style={{ fontSize: '32px', color: '#52c41a', marginBottom: '16px' }}
            />
            <Title level={5}>Earnings Report</Title>
            <Paragraph style={{ marginBottom: '16px' }}>
              Download detailed earnings report
            </Paragraph>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
              Download PDF
            </Button>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ textAlign: 'center', height: '100%' }}>
            <TrophyOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '16px' }} />
            <Title level={5}>Achievements</Title>
            <Paragraph style={{ marginBottom: '16px' }}>
              View your achievements and rewards
            </Paragraph>
            <Button icon={<EyeOutlined />} onClick={() => setAchievementsModalVisible(true)}>
              View All
            </Button>
          </Card>
        </Col>
      </Row>

      {/* Earnings Breakdown */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} lg={16}>
          <Card title="Earnings History">
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Select placeholder="Filter by period" style={{ width: 120 }}>
                  <Option value="today">Today</Option>
                  <Option value="week">This Week</Option>
                  <Option value="month">This Month</Option>
                  <Option value="year">This Year</Option>
                </Select>
                <RangePicker />
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={Array.isArray(earnings) ? earnings : []}
              rowKey="id"
              loading={loading}
              size="middle"
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} earnings`,
                position: ['bottomCenter'],
                showSizeChangerOptions: ['10', '20', '50'],
              }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
              }
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Earnings Breakdown">
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}
              >
                <Text>Delivery Fees</Text>
                <Text strong>
                  NPR {((totalEarnings || 0) - (totalTips || 0) - (totalBonuses || 0)).toFixed(2)}
                </Text>
              </div>
              <Progress percent={75} showInfo={false} />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}
              >
                <Text>Tips</Text>
                <Text strong>NPR {(totalTips || 0).toFixed(2)}</Text>
              </div>
              <Progress percent={15} showInfo={false} strokeColor="#52c41a" />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}
              >
                <Text>Bonuses</Text>
                <Text strong>NPR {(totalBonuses || 0).toFixed(2)}</Text>
              </div>
              <Progress percent={10} showInfo={false} strokeColor="#faad14" />
            </div>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>Total</Text>
              <Text strong style={{ color: '#1890ff' }}>
                NPR {(totalEarnings || 0).toFixed(2)}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Achievements */}
      <Card title="Achievements & Bonuses">
        <Row gutter={[16, 16]}>
          {achievements.map((achievement) => (
            <Col xs={24} sm={12} md={6} key={achievement.id}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  border: achievement.achieved ? '2px solid #52c41a' : '1px solid #d9d9d9',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <TrophyOutlined
                    style={{
                      fontSize: '32px',
                      color: achievement.achieved ? '#52c41a' : '#d9d9d9',
                      marginBottom: '16px',
                    }}
                  />
                  <Title level={5}>{achievement.title}</Title>
                  <Paragraph style={{ fontSize: '0.9rem', marginBottom: '16px' }}>
                    {achievement.description}
                  </Paragraph>
                  <div style={{ marginBottom: '16px' }}>
                    <Progress
                      percent={(achievement.progress / achievement.total) * 100}
                      size="small"
                      strokeColor={achievement.achieved ? '#52c41a' : '#1890ff'}
                    />
                    <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#666' }}>
                      {achievement.progress}/{achievement.total}
                    </div>
                  </div>
                  <div>
                    <Tag color={achievement.achieved ? 'success' : 'blue'}>
                      {achievement.reward}
                    </Tag>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Withdrawal Modal */}
      <Modal
        title="Withdraw Funds"
        open={withdrawModalVisible}
        onCancel={() => setWithdrawModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleWithdraw} layout="vertical">
          <Form.Item
            name="amount"
            label="Withdrawal Amount"
            rules={[
              { required: true, message: 'Please enter withdrawal amount!' },
              { type: 'number', min: 10, message: 'Minimum withdrawal is NPR 10!' },
              { type: 'number', max: totalEarnings, message: 'Insufficient balance!' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `NPR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/NPR\s?|(,*)/g, '')}
              placeholder="Enter amount"
            />
          </Form.Item>

          <Form.Item
            name="bankAccount"
            label="Bank Account"
            rules={[{ required: true, message: 'Please select bank account!' }]}
          >
            <Select placeholder="Select bank account">
              <Option value="account1">****1234 (Checking)</Option>
              <Option value="account2">****5678 (Savings)</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Submit Withdrawal
              </Button>
              <Button onClick={() => setWithdrawModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Achievements Modal */}
      <Modal
        title="All Achievements"
        open={achievementsModalVisible}
        onCancel={() => setAchievementsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setAchievementsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <Row gutter={[16, 16]}>
          {achievements.map((achievement) => (
            <Col xs={24} sm={12} key={achievement.id}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  border: achievement.achieved ? '2px solid #52c41a' : '1px solid #d9d9d9',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <TrophyOutlined
                    style={{
                      fontSize: '32px',
                      color: achievement.achieved ? '#52c41a' : '#d9d9d9',
                      marginBottom: '16px',
                    }}
                  />
                  <Title level={5}>{achievement.title}</Title>
                  <Paragraph style={{ fontSize: '0.9rem', marginBottom: '16px' }}>
                    {achievement.description}
                  </Paragraph>
                  <div style={{ marginBottom: '16px' }}>
                    <Progress
                      percent={(achievement.progress / achievement.total) * 100}
                      size="small"
                      strokeColor={achievement.achieved ? '#52c41a' : '#1890ff'}
                    />
                    <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#666' }}>
                      {achievement.progress}/{achievement.total}
                    </div>
                  </div>
                  <div>
                    <Tag color={achievement.achieved ? 'success' : 'blue'}>
                      {achievement.reward}
                    </Tag>
                    {achievement.achieved && (
                      <Tag color="success" style={{ marginLeft: '8px' }}>
                        ACHIEVED
                      </Tag>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Modal>
    </div>
  );
};

export default Earnings;
